import dayjs from 'dayjs'
import { getDurationFromInterval, IcsDuration, IcsEvent } from 'ts-ics'

import { decodeIcsMultilineText } from '@/entities/schedule'

import { useLocalization } from '@/shared/composables/localization'
import { getTextForDifferentCount } from '@/shared/utils/get-text-for-different-count'
import { getScheduleEventDisplayName } from '@/shared/utils/schedule'

export type FormattedEvent = IcsEvent & {
  uid: string
  summary: string
  displaySummary: string
  start: IcsEvent['start']
  duration?: IcsEvent['duration']
  recurrenceRule?: IcsEvent['recurrenceRule']
  attendees?: IcsEvent['attendees']
  formattedDate: string
  formattedTime: string
  formattedDuration: string
  formattedUntilDate: string
  formattedRecurrenceRule: string
  formattedParticipants: string
}

export function useEventFormatting () {
  const { t, tc } = useLocalization()

  const resolveEventDuration = (event: IcsEvent): IcsDuration | undefined => {
    if (event.duration) {
      return event.duration
    }

    // ICS после экспорта часто содержит DTEND вместо DURATION
    if (event.end?.date && event.start?.date) {
      return getDurationFromInterval(event.start.date, event.end.date)
    }

    return undefined
  }

  const getEventDuration = (event: IcsEvent): string => {
    const duration = resolveEventDuration(event)
    if (!duration) return ''

    const days = duration.days ?? 0
    const hours = (duration.hours ?? 0) + days * 24
    const minutes = duration.minutes ?? 0

    const parts = []
    if (hours > 0) {
      parts.push(getTextForDifferentCount(hours, {
        forOne: tc('common.hour', 0),
        forTwo: tc('common.hour', 1),
        forFive: tc('common.hour', 2),
      }))
    }
    if (minutes > 0) {
      parts.push(getTextForDifferentCount(minutes, {
        forOne: tc('common.minute', 0),
        forTwo: tc('common.minute', 1),
        forFive: tc('common.minute', 2),
      }))
    }

    return parts.join(' ')
  }

  const getEventRecurrenceRule = (event: IcsEvent): string => {
    if (!event.recurrenceRule) return ''

    if (event.recurrenceRule.frequency === 'DAILY') {
      return `${t('calendar.everyDay')} ${t('calendar.in')} ${dayjs(event.start.date).tz().format('HH:mm')}`
    }

    if (event.recurrenceRule.frequency === 'WEEKLY') {
      const weekdays = event.recurrenceRule.byDay?.map(day => day.day)
      if (!weekdays) return ''

      if (weekdays.length === 5 && !weekdays.includes('SU') && !weekdays.includes('SA')) {
        return t('calendar.everyWorkDay')
      }

      const weekDaysLocales = weekdays.map(day => t('common.' + day))
      return `${t('common.by')} ${weekDaysLocales.join(', ')}`
    }

    if (event.recurrenceRule.frequency === 'MONTHLY') {
      return `${t('common.everyDate')} ${event.recurrenceRule.byMonthday?.sort((a, b) => a - b).join(', ')} ${t('common.dayOfEachMonth')}`
    }

    return ''
  }

  const formatEvent = (event: IcsEvent): FormattedEvent => {
    const summary = decodeIcsMultilineText(event.summary) ?? event.summary
    const description = decodeIcsMultilineText(event.description) ?? event.description
    const formattedEvent: FormattedEvent = {
      ...event,
      summary,
      ...(description !== undefined ? { description } : {}),
      displaySummary: getScheduleEventDisplayName(summary, t('common.withoutTitle')),
      formattedDate: dayjs(event.start.date).tz().format('DD.MM.YYYY'),
      formattedTime: dayjs(event.start.date).tz().format('HH:mm'),
      formattedDuration: getEventDuration(event),
      formattedUntilDate: event.recurrenceRule?.until?.date
        ? dayjs(event.recurrenceRule.until.date).tz().format('DD.MM.YYYY')
        : '',
      formattedRecurrenceRule: getEventRecurrenceRule(event),
      formattedParticipants: event.attendees?.map(attendee => attendee.name).join(', ') || '',
    }

    return formattedEvent
  }

  const formatEvents = (events: IcsEvent[]): FormattedEvent[] => {
    return events.map(formatEvent)
  }

  return {
    formatEvent,
    formatEvents,
    getEventDuration,
    getEventRecurrenceRule,
  }
}
